import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Send, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import type { CreateTicketRequest } from "@/types/support";

const ticketSchema = z.object({
  user_email: z.string().email("Please enter a valid email address"),
  user_name: z.string().min(2, "Name must be at least 2 characters"),
  user_organization: z.string().optional(),
  user_role: z.string().optional(),
  subject: z.string().min(5, "Subject must be at least 5 characters"),
  description: z.string().min(10, "Description must be at least 10 characters"),
  inquiry_type: z.string().min(1, "Please select an inquiry type"),
  priority: z.enum(["low", "medium", "high", "urgent"]).default("medium")
});

interface SupportTicketFormProps {
  onTicketCreated?: (ticketNumber: string) => void;
  defaultValues?: Partial<CreateTicketRequest>;
}

const SupportTicketForm = ({ onTicketCreated, defaultValues }: SupportTicketFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user, profile } = useAuth();
  const { toast } = useToast();

  const form = useForm<CreateTicketRequest>({
    resolver: zodResolver(ticketSchema),
    defaultValues: {
      user_email: defaultValues?.user_email || profile?.email || "",
      user_name: defaultValues?.user_name || profile?.name || "",
      user_organization: defaultValues?.user_organization || profile?.organization || "",
      user_role: defaultValues?.user_role || profile?.role || "",
      subject: defaultValues?.subject || "",
      description: defaultValues?.description || "",
      inquiry_type: defaultValues?.inquiry_type || "",
      priority: defaultValues?.priority || "medium"
    }
  });

  const onSubmit = async (data: CreateTicketRequest) => {
    setIsSubmitting(true);
    
    try {
      // Using direct database query to handle TypeScript type mismatch
      const { data: result, error } = await supabase
        .from('support_tickets')
        .insert({
          user_id: user?.id || null,
          user_email: data.user_email,
          user_name: data.user_name,
          user_organization: data.user_organization || null,
          user_role: data.user_role || null,
          subject: data.subject,
          description: data.description,
          inquiry_type: data.inquiry_type,
          priority: data.priority || 'medium',
          ticket_number: '' // Will be auto-generated by trigger
        } as any)
        .select('ticket_number')
        .maybeSingle();

      if (error) throw error;

      const ticketNumber = result?.ticket_number || 'Unknown';

      toast({
        title: "Support ticket submitted",
        description: `Your ticket ${ticketNumber} has been submitted successfully. Our support team will review your request and get back to you.`,
      });

      form.reset();
      onTicketCreated?.(ticketNumber);
    } catch (error) {
      console.error('Error creating support ticket:', error);
      toast({
        title: "Error",
        description: "Failed to create support ticket. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="shadow-lg" style={{ boxShadow: 'var(--shadow-elegant)' }}>
      <CardHeader>
        <CardTitle className="text-2xl">Submit a Support Request</CardTitle>
        <CardDescription>
          Fill out the form below and our support team will review your request.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="user_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Full Name</FormLabel>
                    <FormControl>
                      <Input placeholder="John Doe" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="user_email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="john@healthcare.org" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="user_organization"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Organization</FormLabel>
                    <FormControl>
                      <Input placeholder="Healthcare Organization Name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="user_role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Your Role</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select your role" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="case_manager">Case Manager</SelectItem>
                        <SelectItem value="social_worker">Social Worker</SelectItem>
                        <SelectItem value="discharge_planner">Discharge Planner</SelectItem>
                        <SelectItem value="provider">Assisted Living Provider</SelectItem>
                        <SelectItem value="administrator">Administrator</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="inquiry_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>How can we help?</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select inquiry type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="pricing">Pricing Information</SelectItem>
                        <SelectItem value="technical">Technical Questions</SelectItem>
                        <SelectItem value="partnership">Partnership Opportunities</SelectItem>
                        <SelectItem value="support">Customer Support</SelectItem>
                        <SelectItem value="billing">Billing Issues</SelectItem>
                        <SelectItem value="feature_request">Feature Request</SelectItem>
                        <SelectItem value="bug_report">Bug Report</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="priority"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Priority</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select priority" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="low">Low - General inquiry</SelectItem>
                        <SelectItem value="medium">Medium - Standard request</SelectItem>
                        <SelectItem value="high">High - Important issue</SelectItem>
                        <SelectItem value="urgent">Urgent - Critical problem</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="subject"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Subject</FormLabel>
                  <FormControl>
                    <Input placeholder="Brief description of your request" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Message</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Please provide detailed information about your request..."
                      className="min-h-[120px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button 
              type="submit" 
              className="w-full bg-gradient-to-r from-primary to-primary-glow hover:shadow-lg"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  Submit Request
                  <Send className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default SupportTicketForm;